import React, { useMemo, useState } from "react";
import { sectionTemplates } from "../../../../data/database";

const isBlank = (v) => (v ?? "").toString().trim().length === 0;

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 135;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const getAlignValue = (style) => {
  const v = String(style?.textAlign || style?.alignment || "").toLowerCase().trim();
  if (v === "left" || v === "center" || v === "right") return v;
  return "center";
};

const getAlignClass = (style) => {
  const a = getAlignValue(style);
  if (a === "left") return "text-left";
  if (a === "right") return "text-right";
  return "text-center";
};

const GallerySection3 = ({ config, style }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const tpl = sectionTemplates?.gallery?.template3 || {};
  const cfg = { ...(tpl.config || {}), ...(config || {}) };
  const s = { ...(tpl.style || {}), ...(style || {}) };

  const projects = useMemo(() => {
    if (Array.isArray(cfg?.projects) && cfg.projects.length) return cfg.projects;
    if (Array.isArray(cfg?.featuredProjects) && cfg.featuredProjects.length) return cfg.featuredProjects;
    return [];
  }, [cfg]);

  const currentProject = projects[currentIndex] || null;

  const goToProject = (index) => setCurrentIndex(index);

  const bg = buildBg(s, "bg-gray-900");
  const alignClass = getAlignClass(s);

  const accent = (s?.primaryBtnBg || "").trim();
  const hasAccent = accent.length > 0;

  const badgeStyle = hasAccent ? { backgroundColor: accent } : undefined;
  const resultValueStyle = hasAccent ? { color: accent } : undefined;

  return (
    <section
      dir={cfg?.direction || "ltr"}
      style={{
        ...bg.style,
        ...(hasAccent ? { "--accent": accent } : undefined),
        color: s?.galleryMainTextColor || undefined,
      }}
      className={`py-20 px-4 text-white ${bg.className}`}
    >
      <div className="container mx-auto max-w-6xl">
        <div className={`${alignClass} mb-12`}>
          <h2
            style={{ color: s?.titleColor || undefined }}
            className="text-4xl md:text-5xl font-bold mb-4"
          >
            {cfg?.title || "Our Work"}
          </h2>

          <p
            style={{ color: s?.subtitleColor || undefined }}
            className="text-xl text-gray-300 max-w-2xl mx-auto"
          >
            {cfg?.subtitle || "Explore our portfolio of successful projects"}
          </p>
        </div>

        {!currentProject ? (
          <div
            style={{ color: s?.galleryEmptyStateColor || undefined }}
            className="text-center text-gray-300"
          >
            {cfg?.emptyStateText || "No projects yet. Add projects from the admin editor."}
          </div>
        ) : (
          <div className="mb-8">
            <div className="relative rounded-2xl overflow-hidden mb-6">
              <img
                src={currentProject.image}
                alt={currentProject.title}
                className="w-full h-[400px] md:h-[500px] object-cover"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <div className={`max-w-3xl ${alignClass}`}>
                  <div
                    style={{
                      backgroundColor: s?.galleryBadgeBg || badgeStyle?.backgroundColor || undefined,
                      color: s?.galleryBadgeTextColor || undefined,
                    }}
                    className={`inline-block backdrop-blur-sm px-3 py-1 rounded-full text-xs md:text-sm mb-3 ${
                      hasAccent ? "" : "bg-blue-600/90"
                    }`}
                  >
                    {currentProject.category}
                  </div>

                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3">
                    {currentProject.title}
                  </h3>

                  <p
                    style={{ color: s?.galleryMainTextColor || undefined }}
                    className="text-gray-200 text-sm md:text-base max-w-2xl mx-auto mb-4"
                  >
                    {currentProject.description}
                  </p>

                  {!isBlank(currentProject?.url) && !isBlank(cfg?.primaryButtonText) && (
                    <button
                      type="button"
                      onClick={() => window.open(currentProject.url, "_blank", "noopener,noreferrer")}
                      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold ${
                        hasAccent
                          ? "bg-[var(--accent)] text-white"
                          : "bg-blue-600 hover:bg-blue-700 text-white"
                      }`}
                    >
                      {cfg.primaryButtonText}
                      <span className="opacity-90">→</span>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {Array.isArray(currentProject.results) && currentProject.results.length > 0 && (
              <div className="grid grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto">
                {currentProject.results.map((result, idx) => (
                  <div
                    key={idx}
                    style={{
                      backgroundColor: s?.galleryResultCardBg || undefined,
                      borderColor: s?.galleryResultCardBorder || undefined,
                    }}
                    className={`${alignClass} bg-white/5 rounded-xl p-4 backdrop-blur-sm border border-white/10`}
                  >
                    <div
                      style={{
                        ...(resultValueStyle || {}),
                        color: s?.galleryResultValueColor || resultValueStyle?.color || undefined,
                      }}
                      className={`text-xl md:text-2xl font-bold mb-1 ${
                        hasAccent ? "" : "text-blue-400"
                      }`}
                    >
                      {result.value}
                    </div>
                    <div
                      style={{ color: s?.galleryResultLabelColor || undefined }}
                      className="text-xs text-gray-400"
                    >
                      {result.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {projects.length > 1 && (
          <div className="mt-12">
            <p
              style={{ color: s?.galleryMoreProjectsLabelColor || undefined }}
              className="text-center text-sm text-gray-400 mb-4"
            >
              {cfg?.moreProjectsLabel || "More Projects"}
            </p>

            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {projects.map((project, index) => (
                <button
                  key={project.id}
                  onClick={() => goToProject(index)}
                  style={
                    index === currentIndex && s?.galleryThumbActiveBorder
                      ? { boxShadow: `0 0 0 2px ${s.galleryThumbActiveBorder}` }
                      : undefined
                  }
                  className={`relative group rounded-lg overflow-hidden transition-all ${
                    index === currentIndex
                      ? hasAccent
                        ? "ring-2 ring-[var(--accent)] scale-105"
                        : "ring-2 ring-blue-500 scale-105"
                      : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <div className="aspect-square">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div
                    style={{
                      backgroundColor: s?.galleryThumbOverlayColor || undefined,
                    }}
                    className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default GallerySection3;